import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useMediaQuery } from 'usehooks-ts';

import BrandedImagery from '../../branded-imagery/branded-imagery.component';
import { ExpandButton } from '../../button/expand-button/expand-button';
import { Icon } from '../../icon/icon.component';
import { Title } from '../../title/title.component';
import { Breakpoints, getMediaQuery } from '../../../utils/breakpoints';
import { getComponentFromProps, getPropsFromModel } from '../../../utils/use-child-components';
import { useHasGradientBackground, useHasMidBlueBackground } from '../../../utils/use-has-background-class';

import { UseCasesRowProps } from './use-cases-row.config';

const UseCasesRow = ({
  appliedCssClassNames,
  brandedImagery,
  cqPath,
  grid,
  icon,
  index,
  isFirst,
  isLast,
  title,
}: UseCasesRowProps) => {
  const useCasesContentBlockContainerProps = getPropsFromModel(grid, cqPath, 'grid');
  const UseCasesContentBlockContainer = getComponentFromProps(useCasesContentBlockContainerProps);

  const isMobile = useMediaQuery(getMediaQuery(Breakpoints.MOBILE, 'down'));
  const [expanded, setExpanded] = useState(!isMobile);

  const hasGradientBackground = useHasGradientBackground(appliedCssClassNames);
  const hasMidBlueBackground = useHasMidBlueBackground(appliedCssClassNames);

  const darkBg = useMemo(() => {
    return hasGradientBackground || hasMidBlueBackground;
  }, [hasGradientBackground, hasMidBlueBackground]);

  const styles = useMemo(
    () => ({
      row: {
        'snowflake-use-cases-row': true,
        'snowflake-use-cases-row-expanded': expanded,
        'snowflake-use-cases-row-expanded-on-dark': expanded && darkBg,
        'snowflake-use-cases-row-first-item': isFirst,
        'snowflake-use-cases-row-last-item': isLast,
      },
      icon: {
        'snowflake-icon-blue': !darkBg,
        'snowflake-icon-white': darkBg,
      },
      brandedImagery: {
        'white-imagery-background': !darkBg,
        'blue-imagery-background': darkBg,
        [`use-cases-row-${index || 0 % 4}`]: true,
      },
      title: {
        'snowflake-use-cases-row-title': true,
        'mid-blue-background': darkBg,
      },
    }),
    [darkBg, index, expanded, isFirst, isLast]
  );

  const showUseCasesGrid = useMemo(() => {
    if (!isMobile) {
      return true;
    }

    return expanded;
  }, [isMobile, expanded]);

  useEffect(() => {
    if (isMobile) {
      setExpanded(false);
    }
  }, [isMobile]);

  const toggleContentVisibility = () => {
    if (!isMobile) {
      return;
    }
    setExpanded((current) => {
      return !current;
    });
  };

  return (
    <div className={classNames(styles.row)} data-testid="snowflake-use-cases-row">
      {icon && (
        <div
          data-testid="snowflake-use-cases-row-icon"
          className={classNames(styles.icon, 'snowflake-use-cases-row-icon')}
        >
          <Icon {...icon} />
        </div>
      )}
      {brandedImagery && (
        <div
          data-testid="snowflake-use-cases-row-branded-imagery"
          className={classNames(styles.brandedImagery, 'snowflake-use-cases-row-branded-imagery')}
        >
          <BrandedImagery {...brandedImagery} isInEditor={false} lineWidth="50%" />
        </div>
      )}

      <div className="snowflake-uses-row-title-row">
        {title ? (
          <div
            data-testid="snowflake-use-cases-row-title"
            className={classNames(styles.title)}
            onClick={toggleContentVisibility}
          >
            <Title {...title} isInEditor={false} />
          </div>
        ) : null}

        {isMobile && (
          <ExpandButton
            expanded={expanded}
            darkBg={darkBg}
            onClick={toggleContentVisibility}
            testId="snowflake-use-cases-row-toggle-button"
          />
        )}
      </div>

      {showUseCasesGrid && (
        <div data-testid="use-cases-content-block-container">
          {UseCasesContentBlockContainer && (
            <UseCasesContentBlockContainer
              appliedCssClassNames={appliedCssClassNames}
              {...useCasesContentBlockContainerProps}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default UseCasesRow;
